/**
 * Wiki content-loader. Liest alle .md-files unter src/content/wiki/ via
 * Vite's import.meta.glob (eager string-import → ein chunk in build).
 *
 * Frontmatter wird mit einem mini-parser extrahiert (kein gray-matter
 * dependency — die syntax ist hier strict YAML-ish).
 */

const files = import.meta.glob("../../content/wiki/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

export interface WikiArticle {
  slug: string;
  title: string;
  priority: number;
  category: "rechtliches" | "massnahmen" | "doku" | "quellen" | "sonstiges";
  lastUpdated?: string;
  body: string; // markdown ohne frontmatter
}

function parseFrontmatter(raw: string): { meta: Record<string, string>; body: string } {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/);
  if (!match) {
    return { meta: {}, body: raw };
  }
  const meta: Record<string, string> = {};
  for (const line of match[1]!.split("\n")) {
    const lineMatch = line.match(/^([a-z_]+):\s*(.+)$/i);
    if (!lineMatch) continue;
    meta[lineMatch[1]!] = lineMatch[2]!.trim();
  }
  return { meta, body: match[2]! };
}

function parseArticle(filename: string, raw: string): WikiArticle | null {
  const { meta, body } = parseFrontmatter(raw);
  const slug =
    meta.slug ??
    filename
      .replace(/.*\//, "")
      .replace(/\.md$/, "")
      .replace(/^\d+[-_]/, "");
  if (!meta.title) {
    return null;
  }
  const allowedCategories = ["rechtliches", "massnahmen", "doku", "quellen"] as const;
  const cat = (allowedCategories as readonly string[]).includes(meta.category ?? "")
    ? (meta.category as WikiArticle["category"])
    : "sonstiges";
  return {
    slug,
    title: meta.title,
    priority: parseInt(meta.priority ?? "0", 10) || 0,
    category: cat,
    lastUpdated: meta.last_updated,
    body: body.trim(),
  };
}

const articles: WikiArticle[] = Object.entries(files)
  .map(([filename, raw]) => parseArticle(filename, raw))
  .filter((a): a is WikiArticle => a !== null)
  .sort((a, b) => b.priority - a.priority);

export function listArticles(): WikiArticle[] {
  return articles;
}

export function getArticle(slug: string): WikiArticle | undefined {
  return articles.find((a) => a.slug === slug);
}

export function searchArticles(query: string): WikiArticle[] {
  const q = query.trim().toLowerCase();
  if (!q) return articles;
  return articles.filter(
    (a) => a.title.toLowerCase().includes(q) || a.body.toLowerCase().includes(q)
  );
}
